'use client'

import {
    getProntuarioSummary,
    getTimelineByClient,
    MedicalTimelineEntry,
    ProntuarioSummary,
    TIMELINE_EVENT_TYPE_LABELS,
} from '@/lib/api/prontuario/prontuario-api'
import { format } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import { useEffect, useState } from 'react'
import {
    MdAdd,
    MdArrowForward,
    MdAssignment,
    MdCalendarToday,
    MdLocalHospital,
    MdMedicalServices,
    MdTimeline,
    MdTrendingUp,
    MdWarning,
} from 'react-icons/md'
import { toast } from 'react-toastify'
import { useProntuarioData, useProntuarioNavigate } from './prontuario-context'

export default function ProntuarioResumoPage() {
    const navigate = useProntuarioNavigate()
    const { client, allergies, clientId } = useProntuarioData()

    const [summary, setSummary] = useState<ProntuarioSummary | null>(null)
    const [recentEvents, setRecentEvents] = useState<MedicalTimelineEntry[]>([])
    const [loading, setLoading] = useState(true)

    const basePath = `/person/clients/${clientId}/prontuario`

    useEffect(() => {
        if (clientId) {
            loadData()
        }
    }, [clientId])

    const loadData = async () => {
        if (!clientId) return

        setLoading(true)
        try {
            const [summaryResponse, timelineResponse] = await Promise.all([
                getProntuarioSummary(parseInt(clientId, 10)),
                getTimelineByClient(parseInt(clientId, 10), { limit: 5 }),
            ])

            if (summaryResponse.success) {
                setSummary(summaryResponse.result)
            }

            if (timelineResponse.success) {
                setRecentEvents(
                    Array.isArray(timelineResponse.result)
                        ? timelineResponse.result
                        : []
                )
            }
        } catch (error) {
            toast.error('Erro ao carregar resumo do prontuário')
        } finally {
            setLoading(false)
        }
    }

    const activeAllergiesCount = allergies.filter(
        a => a.status === 'ACTIVE'
    ).length

    if (loading) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[300px] gap-4">
                <div className="loading loading-spinner loading-lg text-secondary"></div>
                <p className="text-base-content/60 text-sm">
                    Carregando resumo...
                </p>
            </div>
        )
    }

    return (
        <div className="flex flex-col gap-6">
            {/* Quick Stats */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {/* Allergies Card */}
                <div
                    onClick={() => navigate(`${basePath}/alergias`)}
                    className="bg-gradient-to-br from-base-100 to-base-200/30 rounded-xl border border-base-300/50 shadow-sm p-5 cursor-pointer hover:shadow-lg transition-all duration-300 group"
                >
                    <div className="flex items-center justify-between mb-3">
                        <div
                            className={`p-3 rounded-xl ${activeAllergiesCount > 0 ? 'bg-error/10' : 'bg-success/10'}`}
                        >
                            <MdWarning
                                className={`text-2xl ${activeAllergiesCount > 0 ? 'text-error' : 'text-success'}`}
                            />
                        </div>
                        <MdArrowForward className="text-base-content/30 group-hover:text-secondary transition-colors" />
                    </div>
                    <div>
                        <p className="text-3xl font-bold text-base-content">
                            {activeAllergiesCount}
                        </p>
                        <p className="text-sm text-base-content/60">
                            Alergias Ativas
                        </p>
                    </div>
                </div>

                {/* Diagnoses Card */}
                <div
                    onClick={() => navigate(`${basePath}/diagnosticos`)}
                    className="bg-gradient-to-br from-base-100 to-base-200/30 rounded-xl border border-base-300/50 shadow-sm p-5 cursor-pointer hover:shadow-lg transition-all duration-300 group"
                >
                    <div className="flex items-center justify-between mb-3">
                        <div className="p-3 bg-info/10 rounded-xl">
                            <MdMedicalServices className="text-2xl text-info" />
                        </div>
                        <MdArrowForward className="text-base-content/30 group-hover:text-secondary transition-colors" />
                    </div>
                    <div>
                        <p className="text-3xl font-bold text-base-content">
                            {summary?.activeDiagnosesCount || 0}
                        </p>
                        <p className="text-sm text-base-content/60">
                            Diagnósticos Ativos
                        </p>
                    </div>
                </div>

                {/* Last Consultation Card */}
                <div
                    onClick={() => navigate(`${basePath}/evolucoes`)}
                    className="bg-gradient-to-br from-base-100 to-base-200/30 rounded-xl border border-base-300/50 shadow-sm p-5 cursor-pointer hover:shadow-lg transition-all duration-300 group"
                >
                    <div className="flex items-center justify-between mb-3">
                        <div className="p-3 bg-secondary/10 rounded-xl">
                            <MdCalendarToday className="text-2xl text-secondary" />
                        </div>
                        <MdArrowForward className="text-base-content/30 group-hover:text-secondary transition-colors" />
                    </div>
                    <div>
                        <p className="text-lg font-bold text-base-content">
                            {summary?.lastConsultationDate
                                ? format(
                                      new Date(summary.lastConsultationDate),
                                      'dd/MM/yyyy',
                                      { locale: ptBR }
                                  )
                                : 'Nenhuma'}
                        </p>
                        <p className="text-sm text-base-content/60">
                            Última Consulta
                        </p>
                    </div>
                </div>

                {/* Last Evolution Card */}
                <div
                    onClick={() => navigate(`${basePath}/evolucoes`)}
                    className="bg-gradient-to-br from-base-100 to-base-200/30 rounded-xl border border-base-300/50 shadow-sm p-5 cursor-pointer hover:shadow-lg transition-all duration-300 group"
                >
                    <div className="flex items-center justify-between mb-3">
                        <div className="p-3 bg-secondary/10 rounded-xl">
                            <MdTrendingUp className="text-2xl text-secondary" />
                        </div>
                        <MdArrowForward className="text-base-content/30 group-hover:text-secondary transition-colors" />
                    </div>
                    <div>
                        <p className="text-lg font-bold text-base-content">
                            {summary?.lastEvolutionDate
                                ? format(
                                      new Date(summary.lastEvolutionDate),
                                      'dd/MM/yyyy',
                                      { locale: ptBR }
                                  )
                                : 'Nenhuma'}
                        </p>
                        <p className="text-sm text-base-content/60">
                            Última Evolução
                        </p>
                    </div>
                </div>
            </div>

            {/* Quick Actions */}
            <div className="bg-gradient-to-br from-base-100 to-base-200/30 rounded-xl border border-base-300/50 shadow-sm overflow-hidden">
                <div className="bg-gradient-to-r from-secondary/10 to-secondary/10 px-6 py-4 border-b border-base-300/50">
                    <h2 className="text-lg font-bold text-base-content">
                        Ações Rápidas
                    </h2>
                </div>
                <div className="p-6">
                    <div className="flex flex-wrap gap-3">
                        <button
                            onClick={() =>
                                navigate(`${basePath}/evolucoes/new`)
                            }
                            className="btn btn-secondary gap-2"
                        >
                            <MdAdd className="text-lg" />
                            Nova Evolução
                        </button>
                        <button
                            onClick={() =>
                                navigate(`${basePath}/anamneses/new`)
                            }
                            className="btn btn-outline btn-secondary gap-2"
                        >
                            <MdAssignment className="text-lg" />
                            Nova Anamnese
                        </button>
                        <button
                            onClick={() => navigate(`${basePath}/alergias`)}
                            className="btn btn-outline gap-2"
                        >
                            <MdWarning className="text-lg" />
                            Registrar Alergia
                        </button>
                        <button
                            onClick={() => navigate(`${basePath}/diagnosticos`)}
                            className="btn btn-outline gap-2"
                        >
                            <MdMedicalServices className="text-lg" />
                            Adicionar Diagnóstico
                        </button>
                        <button
                            onClick={() => navigate(`${basePath}/atestados`)}
                            className="btn btn-outline gap-2"
                        >
                            <MdLocalHospital className="text-lg" />
                            Emitir Atestado
                        </button>
                    </div>
                </div>
            </div>

            {/* Recent Timeline */}
            <div className="bg-gradient-to-br from-base-100 to-base-200/30 rounded-xl border border-base-300/50 shadow-sm overflow-hidden">
                <div className="bg-gradient-to-r from-secondary/10 to-secondary/10 px-6 py-4 border-b border-base-300/50">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                            <MdTimeline className="text-xl text-secondary" />
                            <h2 className="text-lg font-bold text-base-content">
                                Últimos Eventos
                            </h2>
                        </div>
                        <button
                            onClick={() => navigate(`${basePath}/timeline`)}
                            className="btn btn-ghost btn-sm gap-1"
                        >
                            Ver Todos
                            <MdArrowForward className="text-lg" />
                        </button>
                    </div>
                </div>
                <div className="p-6">
                    {!Array.isArray(recentEvents) ||
                    recentEvents.length === 0 ? (
                        <div className="text-center py-8 text-base-content/60">
                            <MdTimeline className="text-4xl mx-auto mb-3 text-base-content/20" />
                            <p>Nenhum evento registrado</p>
                            <p className="text-sm mt-1">
                                Os eventos do prontuário aparecerão aqui
                            </p>
                        </div>
                    ) : (
                        <div className="space-y-4">
                            {recentEvents.map(event => (
                                <div
                                    key={event.id}
                                    className="flex items-start gap-4 p-4 bg-base-200/50 rounded-xl border border-base-300/50"
                                >
                                    <div className="p-2 bg-secondary/10 rounded-lg">
                                        <MdTimeline className="text-xl text-secondary" />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2 mb-1">
                                            <span className="badge badge-sm badge-ghost">
                                                {TIMELINE_EVENT_TYPE_LABELS[
                                                    event.eventType
                                                ] || event.eventType}
                                            </span>
                                            <span className="text-xs text-base-content/60">
                                                {format(
                                                    new Date(event.eventDate),
                                                    "dd/MM/yyyy 'às' HH:mm",
                                                    { locale: ptBR }
                                                )}
                                            </span>
                                        </div>
                                        <h4 className="font-semibold text-base-content">
                                            {event.title}
                                        </h4>
                                        {event.summary && (
                                            <p className="text-sm text-base-content/70 line-clamp-2 mt-1">
                                                {event.summary}
                                            </p>
                                        )}
                                        {event.professional && (
                                            <p className="text-xs text-base-content/60 mt-2">
                                                Por: {event.professional.name}
                                            </p>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            {/* Allergy Alert Section */}
            {activeAllergiesCount > 0 && (
                <div className="bg-error/10 rounded-xl border border-error/30 shadow-sm overflow-hidden">
                    <div className="px-6 py-4 border-b border-error/20">
                        <div className="flex items-center gap-2">
                            <MdWarning className="text-xl text-error" />
                            <h2 className="text-lg font-bold text-error">
                                Alerta de Alergias
                            </h2>
                        </div>
                    </div>
                    <div className="p-6">
                        <div className="flex flex-wrap gap-2">
                            {allergies
                                .filter(a => a.status === 'ACTIVE')
                                .map(allergy => (
                                    <span
                                        key={allergy.id}
                                        className="badge badge-error badge-lg gap-1 text-white"
                                    >
                                        <MdWarning className="text-sm" />
                                        {allergy.allergen}
                                    </span>
                                ))}
                        </div>
                        <button
                            onClick={() => navigate(`${basePath}/alergias`)}
                            className="btn btn-error btn-outline btn-sm mt-4"
                        >
                            Ver Detalhes
                        </button>
                    </div>
                </div>
            )}
        </div>
    )
}
